import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import Swal from 'sweetalert2';
import { Subscription } from 'rxjs';

import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { DoctorsService } from '../../../../services/doctors';

@Component({
  selector: 'app-createdoctor',
  templateUrl: './createdoctor.component.html',
  styleUrl: './createdoctor.component.scss'
})
export class CreatedoctorComponent implements OnInit, OnDestroy {
  data!: FormGroup;
  dataForm: FormData = new FormData();

  contentReady = false;
  isSubmitting = false;

  isMobile = false;
  isAr = false;

  editId!: number;
  isUpdatePage = false;

  selectedImage: File | null = null;
  selectedCertificates: File[] = [];

  imagePreviewUrl: string | null = null;
  existingImageUrl: string | null = null;

  imageErrorMessage = '';
  certificateErrorMessage = '';
  formErrorMessage = '';
  backendValidationErrors: string[] = [];

  private imageObjectUrl: string | null = null;

  private readonly maxImageSize = 2 * 1024 * 1024;
  private readonly maxCertificateSize = 5 * 1024 * 1024;
  private readonly maxCertificatesCount = 20;

  private readonly apiBaseUrl = 'https://backendtest.blueboxpet.com';

  private readonly allowedCertificateExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.pdf'
  ];

  private readonly fieldLabels: Record<string, string> = {
    name: 'الاسم بالكامل',
    mobile: 'رقم الموبايل',
    email: 'البريد الإلكتروني',
    specialty: 'التخصص',
    residence_place: 'مكان الإقامة',
    birth_place: 'مكان الميلاد',
    birth_date: 'تاريخ الميلاد',
    phd_source: 'مصدر الدكتوراه',
    phd_year: 'سنة الدكتوراه',
    master_source: 'مصدر الماجستير',
    master_year: 'سنة الماجستير',
    university: 'الجامعة',
    graduation_year: 'سنة التخرج',
    fellowship: 'الزمالة',
    classification_status: 'حالة التصنيف',
    notes: 'الملاحظات'
  };

  private readonly validationOrder = [
    'name',
    'mobile',
    'email',
    'specialty',
    'residence_place',
    'birth_place',
    'birth_date',
    'phd_source',
    'phd_year',
    'master_source',
    'master_year',
    'university',
    'graduation_year',
    'fellowship',
    'classification_status',
    'notes'
  ];

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private doctorService: DoctorsService,
    private router: Router,
    private route: ActivatedRoute,
    private deviceService: DeviceDetectorService
  ) {}

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') === 'ar';

    this.initForm();
    this.setupDegreeFields();

    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.isUpdatePage = true;
        this.editId = +params['id'];

        this.data.patchValue({
          name: params['name'] || '',
          mobile: params['mobile'] || '',
          email: params['email'] || '',
          specialty: params['specialty'] || '',

          residence_place: params['residence_place'] || '',
          birth_place: params['birth_place'] || '',
          birth_date: params['birth_date'] ? String(params['birth_date']).split('T')[0] : '',

          has_phd: this.asBool(params['has_phd']),
          phd_source: params['phd_source'] || '',
          phd_year: params['phd_year'] || null,

          has_master: this.asBool(params['has_master']),
          master_source: params['master_source'] || '',
          master_year: params['master_year'] || null,

          university: params['university'] || '',
          graduation_year: params['graduation_year'] || null,
          fellowship: params['fellowship'] || '',
          classification_status: params['classification_status'] || '',

          ksa_experience: this.asBool(params['ksa_experience']),
          transfer_of_sponsorship: this.asBool(params['transfer_of_sponsorship']),
          offer: false,
          visa: this.asBool(params['visa']),
          has_dataflow: this.asBool(params['has_dataflow']),

          notes: params['notes'] || '',
          is_doctor: true
        });

        const imageFromParams =
          params['image_url'] ||
          params['image'] ||
          params['image_url'] ||
          params['image'] ||
          null;

        this.existingImageUrl = this.fixImageUrl(imageFromParams);
        this.imagePreviewUrl = this.existingImageUrl;

        this.forceOfferFalse();
      }

      this.contentReady = true;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokeImageObjectUrl();
  }

  initForm(): void {
    this.data = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(255)
        ]
      ],

      mobile: [
        '',
        [
          Validators.required,
          Validators.maxLength(30),
          Validators.pattern(/^[0-9+\-\s()]{7,30}$/)
        ]
      ],

      email: ['', [Validators.email, Validators.maxLength(255)]],
      specialty: ['', [Validators.maxLength(255)]],

      residence_place: ['', [Validators.maxLength(255)]],
      birth_place: ['', [Validators.maxLength(255)]],
      birth_date: [''],

      has_phd: [false],
      phd_source: [''],
      phd_year: [null],

      has_master: [false],
      master_source: [''],
      master_year: [null],

      university: ['', [Validators.maxLength(255)]],
      graduation_year: [null, [Validators.min(1940), Validators.max(2100)]],

      fellowship: ['', [Validators.maxLength(255)]],
      classification_status: ['', [Validators.maxLength(255)]],

      ksa_experience: [false],
      transfer_of_sponsorship: [false],
      offer: [false],
      visa: [false],
      has_dataflow: [false],

      notes: ['', [Validators.maxLength(3000)]],

      add_by: [localStorage.getItem('username') || 'admin'],
      is_doctor: [true]
    });
  }

  onSubmit(): void {
    this.resetErrors();
    this.forceOfferFalse();

    if (this.data.invalid) {
      this.data.markAllAsTouched();

      const firstError = this.getFirstFormError();
      this.formErrorMessage = firstError;

      Swal.fire({
        icon: 'error',
        title: 'يوجد خطأ في البيانات',
        text: firstError,
        confirmButtonText: 'تمام'
      });

      this.scrollToFirstInvalid();
      return;
    }

    if (this.selectedCertificates.length > this.maxCertificatesCount) {
      this.certificateErrorMessage = `لا يمكن رفع أكثر من ${this.maxCertificatesCount} ملف`;
      this.formErrorMessage = this.certificateErrorMessage;
      return;
    }

    const payload = this.buildFormData();

    this.isUpdatePage ? this.onEdit(payload) : this.onAdd(payload);
  }

  private buildFormData(): FormData {
    const fd = new FormData();

    const formValue = {
      ...this.data.getRawValue(),
      offer: false,
      is_doctor: true,
      add_by: localStorage.getItem('username') || 'admin'
    };

    Object.entries(formValue).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        fd.append(key, value ? '1' : '0');
        return;
      }

      if (value !== null && value !== undefined && value !== '') {
        fd.append(key, String(value));
      }
    });

    fd.set('offer', '0');

    if (this.selectedImage) {
      fd.append('image', this.selectedImage);
    }

    this.selectedCertificates.forEach(file => {
      fd.append('certificates[]', file);
    });

    return fd;
  }

  onImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';

    this.imageErrorMessage = '';
    this.formErrorMessage = '';

    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/webp'
    ];

    if (!allowedTypes.includes(file.type)) {
      this.selectedImage = null;
      this.imagePreviewUrl = this.existingImageUrl;

      this.imageErrorMessage = 'الصورة يجب أن تكون JPG أو PNG أو WEBP فقط';
      this.formErrorMessage = this.imageErrorMessage;
      return;
    }

    if (file.size > this.maxImageSize) {
      this.selectedImage = null;
      this.imagePreviewUrl = this.existingImageUrl;

      this.imageErrorMessage = 'حجم الصورة يجب ألا يزيد عن 2 ميجا';
      this.formErrorMessage = this.imageErrorMessage;
      return;
    }

    this.selectedImage = file;
    this.revokeImageObjectUrl();

    this.imageObjectUrl = URL.createObjectURL(file);
    this.imagePreviewUrl = this.imageObjectUrl;
  }

  removeImage(): void {
    this.selectedImage = null;
    this.imageErrorMessage = '';
    this.revokeImageObjectUrl();
    this.imagePreviewUrl = this.existingImageUrl;
  }

  onCertificatesChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';

    this.certificateErrorMessage = '';
    this.formErrorMessage = '';

    if (!files.length) return;

    const totalAfterAdd = this.selectedCertificates.length + files.length;

    if (totalAfterAdd > this.maxCertificatesCount) {
      this.certificateErrorMessage = `لا يمكن رفع أكثر من ${this.maxCertificatesCount} ملف`;
      this.formErrorMessage = this.certificateErrorMessage;
      return;
    }

    for (const file of files) {
      if (!this.isValidCertificateFile(file)) {
        this.certificateErrorMessage =
          'الملفات المسموحة للشهادات: صور JPG / PNG / WEBP أو PDF فقط';
        this.formErrorMessage = this.certificateErrorMessage;
        return;
      }

      if (file.size > this.maxCertificateSize) {
        this.certificateErrorMessage = `الملف "${file.name}" حجمه أكبر من 5 ميجا`;
        this.formErrorMessage = this.certificateErrorMessage;
        return;
      }
    }

    this.selectedCertificates = [...this.selectedCertificates, ...files];
  }

  removeCertificate(index: number): void {
    this.selectedCertificates = this.selectedCertificates.filter(
      (_, i) => i !== index
    );

    this.certificateErrorMessage = '';
    this.formErrorMessage = '';
  }

  clearCertificates(): void {
    this.selectedCertificates = [];
    this.certificateErrorMessage = '';
    this.formErrorMessage = '';
  }

  trackByFile(index: number, file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}-${index}`;
  }

  onAdd(payload: FormData): void {
    this.isSubmitting = true;
    this.contentReady = false;

    this.doctorService.create(payload).subscribe({
      next: () => {
        this.isSubmitting = false;

        Swal.fire({
          icon: 'success',
          title: 'تم إنشاء الدكتور',
          showConfirmButton: false,
          timer: 1500
        });

        this.router.navigateByUrl('/doctors');
      },
      error: err => {
        this.isSubmitting = false;
        this.contentReady = true;
        this.showServerError(err);
      }
    });
  }

  onEdit(payload: FormData): void {
    this.isSubmitting = true;
    this.contentReady = false;

    const username = localStorage.getItem('username') || 'Unknown';
    payload.append('editor', username);

    this.doctorService.update(this.editId, payload).subscribe({
      next: () => {
        this.isSubmitting = false;

        Swal.fire({
          icon: 'success',
          title: 'تم تعديل الدكتور',
          showConfirmButton: false,
          timer: 1500
        });

        this.router.navigateByUrl('/doctors');
      },
      error: err => {
        this.isSubmitting = false;
        this.contentReady = true;
        this.showServerError(err);
      }
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.data.get(controlName);

    return Boolean(
      control &&
      control.invalid &&
      (control.dirty || control.touched)
    );
  }

  getError(controlName: string): string {
    const control = this.data.get(controlName);

    if (!control || !control.errors) return '';

    const label = this.fieldLabels[controlName] || controlName;

    if (control.errors['required']) {
      return `${label} مطلوب`;
    }

    if (control.errors['email']) {
      return `${label} غير صحيح`;
    }

    if (control.errors['minlength']) {
      return `${label} يجب ألا يقل عن ${control.errors['minlength'].requiredLength} حروف`;
    }

    if (control.errors['maxlength']) {
      return `${label} يجب ألا يزيد عن ${control.errors['maxlength'].requiredLength} حرف`;
    }

    if (control.errors['min']) {
      return `${label} يجب ألا يقل عن ${control.errors['min'].min}`;
    }

    if (control.errors['max']) {
      return `${label} يجب ألا يزيد عن ${control.errors['max'].max}`;
    }

    if (control.errors['pattern']) {
      if (controlName === 'mobile') {
        return 'رقم الموبايل غير صحيح. اكتب أرقام فقط أو + في البداية';
      }

      return `${label} غير صحيح`;
    }

    return `${label} غير صحيح`;
  }

  private getFirstFormError(): string {
    for (const controlName of this.validationOrder) {
      const control = this.data.get(controlName);

      if (control?.invalid) {
        return this.getError(controlName);
      }
    }

    return 'من فضلك راجع البيانات المطلوبة';
  }

  private showServerError(err: any): void {
    const laravelErrors = err?.error?.errors;

    this.backendValidationErrors = [];

    if (laravelErrors) {
      Object.keys(laravelErrors).forEach(key => {
        const messages = laravelErrors[key];

        if (Array.isArray(messages)) {
          messages.forEach(message => {
            this.backendValidationErrors.push(`${this.translateServerField(key)}: ${message}`);
          });
        }
      });

      this.formErrorMessage =
        this.backendValidationErrors[0] || 'فشل حفظ البيانات بسبب خطأ في التحقق';

      Swal.fire({
        icon: 'error',
        title: 'خطأ من السيرفر',
        text: this.formErrorMessage,
        confirmButtonText: 'تمام'
      });

      return;
    }

    const message =
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      'فشل حفظ البيانات';

    this.formErrorMessage = message;

    Swal.fire({
      icon: 'error',
      title: 'خطأ',
      text: message,
      confirmButtonText: 'تمام'
    });
  }

  private translateServerField(key: string): string {
    const cleanKey = key.replace(/\.\d+/g, '');

    const map: Record<string, string> = {
      name: 'الاسم بالكامل',
      mobile: 'رقم الموبايل',
      email: 'البريد الإلكتروني',
      specialty: 'التخصص',
      university: 'الجامعة',
      graduation_year: 'سنة التخرج',
      has_master: 'حاصل على ماجستير',
      master_source: 'مصدر الماجستير',
      master_year: 'سنة الماجستير',
      has_phd: 'حاصل على دكتوراه',
      phd_source: 'مصدر الدكتوراه',
      phd_year: 'سنة الدكتوراه',
      fellowship: 'الزمالة',
      classification_status: 'حالة التصنيف',
      ksa_experience: 'خبرة السعودية',
      residence_place: 'مكان الإقامة',
      birth_place: 'مكان الميلاد',
      birth_date: 'تاريخ الميلاد',
      transfer_of_sponsorship: 'نقل الكفالة',
      visa: 'التأشيرة',
      has_dataflow: 'الداتافلو',
      notes: 'الملاحظات',
      image: 'صورة الدكتور',
      certificates: 'الشهادات',
      'certificates.*': 'ملفات الشهادات',
      add_by: 'تمت الإضافة بواسطة',
      is_doctor: 'نوع التسجيل'
    };

    return map[cleanKey] || cleanKey;
  }

  private setupDegreeFields(): void {
    this.updateDegreeControls(
      'phd_source',
      'phd_year',
      Boolean(this.data.get('has_phd')?.value)
    );

    this.updateDegreeControls(
      'master_source',
      'master_year',
      Boolean(this.data.get('has_master')?.value)
    );

    const phdSub = this.data.get('has_phd')?.valueChanges.subscribe(value => {
      this.updateDegreeControls('phd_source', 'phd_year', Boolean(value));
    });

    const masterSub = this.data.get('has_master')?.valueChanges.subscribe(value => {
      this.updateDegreeControls('master_source', 'master_year', Boolean(value));
    });

    if (phdSub) this.subscriptions.add(phdSub);
    if (masterSub) this.subscriptions.add(masterSub);
  }

  private updateDegreeControls(
    sourceControlName: string,
    yearControlName: string,
    checked: boolean
  ): void {
    const sourceControl = this.data.get(sourceControlName);
    const yearControl = this.data.get(yearControlName);

    if (!sourceControl || !yearControl) return;

    if (checked) {
      sourceControl.setValidators([
        Validators.required,
        Validators.maxLength(255)
      ]);

      yearControl.setValidators([
        Validators.required,
        Validators.min(1940),
        Validators.max(2100)
      ]);
    } else {
      sourceControl.clearValidators();
      yearControl.clearValidators();

      sourceControl.setValue('', { emitEvent: false });
      yearControl.setValue(null, { emitEvent: false });
    }

    sourceControl.updateValueAndValidity({ emitEvent: false });
    yearControl.updateValueAndValidity({ emitEvent: false });
  }

  private scrollToFirstInvalid(): void {
    setTimeout(() => {
      const firstInvalid = document.querySelector('.is-invalid');

      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);
  }

  private resetErrors(): void {
    this.formErrorMessage = '';
    this.backendValidationErrors = [];
    this.imageErrorMessage = '';
    this.certificateErrorMessage = '';
  }

  private forceOfferFalse(): void {
    this.data.get('offer')?.setValue(false, { emitEvent: false });
  }

  private isValidCertificateFile(file: File): boolean {
    const fileName = file.name.toLowerCase();

    return this.allowedCertificateExtensions.some(extension =>
      fileName.endsWith(extension)
    );
  }

  private asBool(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private fixImageUrl(path: string | null): string | null {
    if (!path) return null;

    let value = String(path).trim();

    if (!value) return null;

    if (value.startsWith('http://') || value.startsWith('https://')) {
      if (value.includes('/storage/app/public/')) {
        return value;
      }

      if (value.includes('/storage/')) {
        return value.replace('/storage/', '/storage/app/public/');
      }

      return value;
    }

    value = value
      .replace(/^\/+/, '')
      .replace(/^storage\/app\/public\//, '')
      .replace(/^storage\//, '')
      .replace(/^app\/public\//, '')
      .replace(/^public\//, '');

    return `${this.apiBaseUrl}/storage/app/public/${value}`;
  }

  private revokeImageObjectUrl(): void {
    if (this.imageObjectUrl) {
      URL.revokeObjectURL(this.imageObjectUrl);
      this.imageObjectUrl = null;
    }
  }
}